import Cookies from 'universal-cookie';
import store from 'store';
import { config } from 'config';

export const currencyFormatter = (data: number | string) => {
  if (typeof data === 'string') {
    data = parseInt(data)
  }
  return `$${data.toFixed(2)}`
}

export const statusFormatter = (status: string) => {
  switch (status) {
    case 'F':
      return 'Closed'
    case 'P':
      return 'Open'
  }
}

export const issuingDepartment = [
  'New Delivery',
  'Used Delivery',
  'New CSI',
  'Used CSI',
  'SVC CSI',
  'SVC RO',
  'SVC PDI',
  'B/S RO',
  'Co. Vehicle - SVC',
  'Co. Vehicle - Demo Units',
  'Co. Vehicle - Parts',
  'Co. Vehicle - B/S',
  'Co. Vehicle - Rental',
  'Co. Vehicle - Other',
  'Other - Enter a Reason'
]

const names = [
  'status',
  'createdate',
  'stknum',
  'vehmake',
  'vehmodel',
  'issuedto',
  'departmentissue',
  'dollarsallowed',
  'totalspent',
  'createdby',
  'vehyr'
]

export const checkInitialValues = (initVals: any) => {
  let val: boolean | string
  for (val in initVals) {
    initVals[val] = initVals[val] ?? ''
  }
  return initVals
}

export const addActions = (cols: any) => {
  cols.unshift({
    headerName: 'Actions',
    headerClass: 'text-center',
    cellClass: 'no-focus',
    cellRenderer: 'actionsRenderer',
    colId: 'voucherAction',
    maxWidth: 115,
    minWidth: 115,
    pinned: 'left'
  })
}

export const showCols = (name: string): { hide: boolean } => {
  const names = [
    'clientid',
    'status',
    'createdate',
    'stknum',
    'vehmake',
    'vehmodel',
    'issuedto',
    'departmentissue',
    'amtnottoexceed',
    'totalspent',
    'createdby',
    'vehyr'
  ]
  const hide = !names.includes(name)
  return { hide }
}

export const setWidth = (name: string) => {
  switch (name) {
    case 'vehmake':
      return 70
    case 'status':
    case 'vehyr':
      return 50
    case 'dollarsallowed':
    case 'totalspent':
    case 'createdate':
      return 80
    default:
      return 90
  }
}

export const nameExchange: { [key: string]: string } = {
  voucherid: 'VoucherID',
  clientid: 'Location',
  createdby: 'Created By',
  createdate: 'Create Date',
  gallonsallowed: 'Gallons Allowed',
  dollarsallowed: 'Dollars Allowed',
  gallonsused: 'GallonsUsed',
  totalspent: 'Total Spent',
  issuedto: 'Issued To',
  status: 'Voucher Status',
  pricepergallon: 'PricePerGallon',
  stknum: 'Stock Number',
  vinnum: 'VIN',
  vehyr: 'Year',
  vehmake: 'Make',
  vehmodel: 'Model',
  vehcolor: 'Color',
  amtnottoexceed: 'Allotted Amount',
  fillup: 'FillUp',
  vtype: 'VehicleStockType',
  completedate: 'CompletedDate',
  userID: 'UserName',
  void: 'Void',
  voidreason: 'VoidReason',
  completedby: 'CompletedBy',
  changedby: 'ChangedBy',
  changeddate: 'ChangedDate',
  departmentissue: 'Department',
  ronum: 'RepairOrderNumber',
  otherreason: 'ReasonForOther'
}

export const shiftHeaders = (name: keyof typeof nameExchange) =>
  nameExchange[name]

export const value = (curr: string) => {
  switch (curr) {
    case 'dollarsallowed':
    case 'totalspent':
    case 'amtnottoexceed':
      return currency
    case 'status':
      return status
    default:
      return null
  }
}

export const type = (curr: string) => {
  switch (curr) {
    case 'dollarsallowed':
      return 'rightAligned'
    default:
      return null
  }
}

const status = (val: any) => {
  const {
    colDef: { field },
    data: { status }
  } = val
  if (field !== 'status') {
    return val.value
  }
  switch (status) {
    case 'P':
      return 'Open'
    case 'F':
      return 'Closed'
    case 'V':
      return 'Void'
    default:
      return null
  }
}

const currency = (val: any) => {
  return `$${val.value}`
}

export const getMetricValues = (data: any) => {
  return {
    open: ovOpenCalc(data),
    closed: ovClosedCalc(data),
    outstanding: ovOutstanding(data)
  }
}

const ovOpenCalc = (vouchers: any) =>
  vouchers.reduce((acc: any, curr: any) => {
    if (curr.status === 'P') {
      return acc + 1
    }
    return acc
  }, 0)

const ovClosedCalc = (vouchers: any) =>
  vouchers.reduce((acc: any, curr: any) => {
    if (curr.status === 'F') {
      return acc + 1
    }
    return acc
  }, 0)

const ovOutstanding = (vouchers: any) => {
  return vouchers.reduce((acc: any, curr: any) => {
    if (curr.status === 'P') {
      return acc + +curr.dollarsallowed
    }
    return acc
  }, 0)
}



/*
 * Session recovery, run when the API says the session is gone. The API has
 * already expired every copy of the dead qid cookie in that response, so:
 *   1. ask HALO auth to refresh - it mints a fresh qid; reload and carry on
 *   2. if that fails or didn't help, sign out of HALO and send the user to login,
 *      which mints a fresh qid and brings them back here
 * Each step runs at most once a minute (marker cookies expire after 60s), so a
 * session that still can't be recovered lands on the error screen, never a loop.
 */
const REFRESH_MARKER = 'foSessionRefreshAt';
const LOGIN_MARKER = 'foLoginRedirectAt';
const MARKER_MAX_AGE_SECONDS = 60;

let recoveryTimer: number | undefined;

// Called once per failed request; several fail together on page load, and each
// response may carry cookie-clearing headers, so wait until they have all landed
export const logout = () => {
  window.clearTimeout(recoveryTimer);
  recoveryTimer = window.setTimeout(recoverSession, 300);
};

// Signed in fine, so a later expired session gets the full recovery again
export const clearSessionRecoveryMarkers = () => {
  const cookies = new Cookies();
  cookies.remove(REFRESH_MARKER, { path: '/' });
  cookies.remove(LOGIN_MARKER, { path: '/' });
};

const recoverSession = async () => {
  const cookies = new Cookies();
  const authBase = (config.authServiceUrl || '').replace(/\/+$/, '');
  const postToAuth = (path: string) =>
    fetch(`${authBase}/api/v1/auth/${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });

  cookies.set('showlogin', 'true', { domain: '.dealerops.com' });
  cookies.remove('foDateRange');

  store.remove('foStartDate');
  store.remove('foEndDate');
  store.remove('foAccess');
  store.remove('foRefresh');

  // 1. Silent refresh - the user never notices
  if (authBase && !cookies.get(REFRESH_MARKER)) {
    cookies.set(REFRESH_MARKER, String(Date.now()), { path: '/', maxAge: MARKER_MAX_AGE_SECONDS });
    try {
      const response = await postToAuth('refresh');
      if (response.ok) {
        window.location.reload();
        return;
      }
    } catch {
      // auth unreachable - fall through to a full login
    }
  }

  // Already sent them through login within the last minute and it still failed -
  // stop here (error screen) rather than loop
  if (cookies.get(LOGIN_MARKER)) {
    return;
  }
  cookies.set(LOGIN_MARKER, String(Date.now()), { path: '/', maxAge: MARKER_MAX_AGE_SECONDS });

  // 2. Sign out of HALO so the login page asks for credentials instead of
  // bouncing straight back, then log in for a fresh session
  if (authBase) {
    try {
      await postToAuth('logout');
    } catch {
      // best effort - login still works if this fails
    }
  }

  const loginUrl = `${(config.webUrl || window.location.origin).replace(/\/+$/, '')}/auth/login`;
  window.location.replace(`${loginUrl}?redirect=${encodeURIComponent(window.location.href)}`);
};


